import { invalidateCachePartialMatch } from "@sg/query";
import { useMutation, useQuery } from "react-query";
import { z } from "zod";
import { agent } from "~/util/agent";
import { ReconciliationRunSchema } from "./reconciliationSchema";

const POSITION_BREAKS_KEY = "position-breaks";
const RECONCILIATION_RUNS_KEY = "position-reconciliation-runs";

const PositionBreakSchema = z.object({
  id: z.string().uuid(),
  venue_account_id: z.string().uuid(),
  venue_symbol: z.string(),
  instrument_name: z.string().nullable(),
  status: z.enum(["Open", "Accepted", "Resolved"]),
  first_seen_at: z.string(),
  last_seen_at: z.string(),
  age_seconds: z.number().int(),
  last_residual: z.string(),
  last_residual_notional: z.string().nullable(),
  max_residual_notional: z.string().nullable(),
  last_reason: z.string().nullable(),
  needs_approval: z.boolean(),
  needs_attention: z.boolean(),
  awaiting_verification: z.boolean(),
  pending_action: z.enum(["Accept", "Resolve"]).nullable(),
  pending_actor_id: z.string().uuid().nullable(),
  pending_note: z.string().nullable(),
  pending_at: z.string().nullable(),
  accepted_by: z.string().uuid().nullable(),
  resolved_by: z.string().nullable(),
  approved_by: z.string().uuid().nullable(),
  note: z.string().nullable(),
  resolved_at: z.string().nullable(),
  resolution_verified_at: z.string().nullable(),
  reopened_count: z.number().int(),
  opened_run_id: z.string().uuid(),
  last_run_id: z.string().uuid(),
});

const PositionBreakDetailSchema = z.object({
  position_break: PositionBreakSchema,
  runs: z.array(ReconciliationRunSchema),
});

const PositionBreakActionSchema = z.object({
  outcome: z.enum(["Completed", "PendingApproval"]),
  position_break: PositionBreakSchema,
});

type PositionBreakListStatus = "attention" | "open" | "accepted" | "resolved" | "all";

function breakActionOptions(note?: string) {
  const trimmed = note?.trim();
  if (trimmed) {
    return { labels: ["updated", "position break"], json: { note: trimmed } };
  }
  return { labels: ["updated", "position break"] };
}

const usePositionBreaks = ({
  status,
  venueAccountId,
}: {
  status: PositionBreakListStatus;
  venueAccountId?: string;
}) =>
  useQuery([POSITION_BREAKS_KEY, status, venueAccountId], async () => {
    const params = new URLSearchParams();
    params.set("status", status);
    if (venueAccountId) {
      params.set("venue_account_id", venueAccountId);
    }
    const data = await agent(`venue_book/breaks?${params.toString()}`).json<unknown>();
    return z.array(PositionBreakSchema).parse(data);
  });

const usePositionBreak = (id?: string) =>
  useQuery(
    [POSITION_BREAKS_KEY, id],
    async () => {
      const data = await agent(`venue_book/breaks/${id}`).json<unknown>();
      return PositionBreakDetailSchema.parse(data);
    },
    { enabled: !!id }
  );

const useReconciliationRuns = (filters: {
  venueAccountId?: string;
  venueSymbol?: string;
  cadence?: "" | "Cut" | "Intraday";
  from?: string;
  to?: string;
  enabled?: boolean;
}) => {
  const { enabled = true, ...queryFilters } = filters;
  return useQuery(
    [RECONCILIATION_RUNS_KEY, queryFilters],
    async () => {
      const params = new URLSearchParams();
      if (queryFilters.venueAccountId) {
        params.set("venue_account_id", queryFilters.venueAccountId);
      }
      if (queryFilters.venueSymbol) {
        params.set("venue_symbol", queryFilters.venueSymbol);
      }
      if (queryFilters.cadence) {
        params.set("cadence", queryFilters.cadence);
      }
      if (queryFilters.from && queryFilters.to) {
        params.set("from", queryFilters.from);
        params.set("to", queryFilters.to);
      }
      const data = await agent(`venue_book/reconciliations?${params.toString()}`).json<unknown>();
      return z.array(ReconciliationRunSchema).parse(data);
    },
    { enabled }
  );
};

const useAcceptPositionBreak = () =>
  useMutation(
    async (input: { id: string; note?: string }) => {
      const data = await agent
        .post(`venue_book/breaks/${input.id}/accept`, breakActionOptions(input.note))
        .json<unknown>();
      return PositionBreakActionSchema.parse(data);
    },
    {
      onSuccess() {
        invalidateCachePartialMatch([POSITION_BREAKS_KEY]);
        invalidateCachePartialMatch([RECONCILIATION_RUNS_KEY]);
      },
    }
  );

const useResolvePositionBreak = () =>
  useMutation(
    async (input: { id: string; note?: string }) => {
      const data = await agent
        .post(`venue_book/breaks/${input.id}/resolve`, breakActionOptions(input.note))
        .json<unknown>();
      return PositionBreakActionSchema.parse(data);
    },
    {
      onSuccess() {
        invalidateCachePartialMatch([POSITION_BREAKS_KEY]);
        invalidateCachePartialMatch([RECONCILIATION_RUNS_KEY]);
      },
    }
  );

export {
  PositionBreakSchema,
  ReconciliationRunSchema,
  useAcceptPositionBreak,
  usePositionBreak,
  usePositionBreaks,
  useReconciliationRuns,
  useResolvePositionBreak,
};
